import { FC, ReactNode, useState } from 'react';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Space,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import {
	FileData,
	ProspectBankStatement,
} from '@/libs/fleet-api/services/types';
import { useForm } from '@/libs/antd/form';
import { useFRMPraSurveyInput } from './PraSurveyInputPage';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	DeleteOutlined,
	EditOutlined,
	FileSearchOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import {
	useSelectBankStatementType,
	useSelectMasterBank,
} from '@/libs/fleet-api/hooks/master-data';
import {
	addBankStatement,
	deleteBankStatement,
	updateBankStatement,
} from '@/libs/fleet-api/services/survey';
import { uploader } from '@/libs/fleet-api/uploader';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useModals } from '@/components/modals/ModalProvider';
import rule from '@/libs/antd/rules';
import { useApp } from '@/App';

type FRMPraSurveyInputBankStatementFormProps = {
	children?: ReactNode;
	onNext: () => void;
	onPrev: () => void;
};

type BankStatementForm = {
	input?: {
		id?: number;
		accountNo?: string;
		bankStatementPeriod?: string;
		bankId?: number;
		accountTypeId?: number;
		accountType?: { id: number; value: string };
		bankName?: { id: number; value: string };
		bankStatementFile?: FileData;
		isActive?: boolean;
	};
	items: ProspectBankStatement[];
};

export const useFRMPraSurveyInputBankStatementForm = () => {
	return useForm<BankStatementForm>();
};

const FRMPraSurveyInputBankStatementForm: FC<
	FRMPraSurveyInputBankStatementFormProps
> = ({ children, onNext = () => false }) => {
	const { mainInformation, bankStatementForm, prospectBankStatements } =
		useFRMPraSurveyInput();
	const prospectId = mainInformation?.prospectId;
	const { form, value, fieldName } = bankStatementForm;
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const { notification } = useApp();
	const { modal: modalConfirm, openBankStatementModal } = useModals();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const selectBankStatementType = useSelectBankStatementType();

	const selectMasterBank = useSelectMasterBank();

	const addBankStatementMutation = useMutation({
		mutationFn: addBankStatement,
	});

	const updateBankStatementMutation = useMutation({
		mutationFn: updateBankStatement,
	});

	const deleteBankStatementMutation = useMutation({
		mutationFn: deleteBankStatement,
	});

	const handleSubmit = async ({ input, items }: BankStatementForm) => {
		if (input && prospectId) {
			const payload = {
				prospectId: prospectId,
				bankId: input.bankName!.id.toString(),
				accountTypeId: input.accountTypeId!.toString(),
				accountNo: input.accountNo!,
				bankStatementPeriod: input.bankStatementPeriod!,
				bankStatementFile: input.bankStatementFile!,
				isActive: input.isActive ?? true,
			};
			if (modal?.type === 'add') {
				const res = await addBankStatementMutation.mutateAsync(payload);
				const newInput = {
					...input,
					bankStatementPeriod: dayjs(input.bankStatementPeriod)
						.utc(true)
						.toISOString(),
				};
				form.setFieldsValue({
					items: [...items, { ...newInput!, id: res.data }],
				});
				setModal(null);
				form.resetFields([fieldName(['input'])]);
			} else if (modal?.type === 'edit') {
				const newInput = {
					...input,
					bankStatementPeriod: dayjs(input.bankStatementPeriod)
						.utc(true)
						.toISOString(),
				};
				form.setFieldsValue({
					items: items.map((item, index) => {
						return index === modal.index ? newInput! : item;
					}),
				});
				await updateBankStatementMutation.mutateAsync({
					id: input!.id!,
					...payload,
				});
				setModal(null);
				form.resetFields([fieldName('input')]);
			}
		}
	};

	const handleSubmitBankStatement = () => {
		onNext();
	};

	const getInitialValues = () => {
		return {
			items:
				prospectBankStatements?.map((data: ProspectBankStatement) => {
					return {
						id: data.id,
						accountNo: data.accountNo,
						bankStatementPeriod: data.bankStatementPeriod,
						totalCredit: data.totalCredit,
						totalDebit: data.totalDebit,
						averageBalance: data.averageBalance,
						bankName: data.bankName,
						bankId: data.bankName?.id,
						accountTypeId: data.accountType.id,
						accountType: data.accountType,
						bankStatementFile: data.bankStatementFile,
					} as ProspectBankStatement;
				}) ?? [],
			input: undefined,
		} as BankStatementForm;
	};

	const isLoading =
		addBankStatementMutation.isPending ||
		updateBankStatementMutation.isPending ||
		deleteBankStatementMutation.isPending;

	return (
		<Form.Provider>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				initialValues={getInitialValues()}
				form={form}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['items'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ resetFields, getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as BankStatementForm;
						return (
							<Space {...ANTD_DEFAULT_PROPS.SPACE}>
								<Table
									bordered
									loading={isLoading}
									dataSource={
										values?.items?.map((item, key) => {
											return { ...item, key };
										}) ?? []
									}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											width: 1,
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	openBankStatementModal(record);
																}}
															>
																<FileSearchOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalConfirm.confirm({
																			title:
																				'Anda yakin akan menghapus data ini?',
																		});
																	if (confirmDelete) {
																		await deleteBankStatementMutation.mutateAsync(
																			record.id!,
																		);
																		notification.success({
																			message: 'Success',
																			description: 'Data has been deleted',
																		});
																		form.setFieldsValue({
																			items: values.items.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
										},
										{
											title: 'Jenis Rekening Bank',
											dataIndex: ['accountType', 'value'],
										},
										{
											title: 'Nama Bank',
											dataIndex: ['bankName', 'value'],
										},
										{
											title: 'Account Number',
											dataIndex: ['accountNo'],
										},
										{
											title: 'Periode',
											render: (_, record) => {
												const date = record.bankStatementPeriod;
												return dayjs(date).format('MM/YYYY');
											},
										},
										{
											title: 'Total Debet',
											dataIndex: ['totalDebit'],
										},
										{
											title: 'Total Kredit',
											dataIndex: ['totalCredit'],
										},
										{
											title: 'Average Saldo',
											dataIndex: ['averageBalance'],
										},
									]}
									pagination={false}
								/>

								{children}

								<Space
									style={{ display: 'flex', justifyContent: 'end' }}
									size="large"
								>
									<Space
										style={{ display: 'flex', justifyContent: 'end' }}
										size="middle"
									>
										<Button
											style={{
												width: 120,
												display: 'block',
											}}
											loading={isLoading}
											onClick={() => setModal({ type: 'add' })}
										>
											ADD
										</Button>
										<Button
											type="primary"
											style={{
												width: 200,
												display: 'block',
											}}
											loading={isLoading}
											disabled={(value?.items?.length || 0) < 1}
											onClick={handleSubmitBankStatement}
										>
											NEXT
										</Button>
									</Space>
								</Space>

								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									onOk={() => {
										form?.submit();
									}}
									onCancel={() => {
										setModal(null);
										resetFields([fieldName('input')]);
									}}
								>
									<FleetTitle>Add Rekening Bank</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'accountType'])}
												noStyle
											/>
											<Form.Item
												label="Jenis Rekening Bank"
												name={fieldName(['input', 'accountTypeId'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Select
													{...selectBankStatementType}
													options={
														!values.input?.accountTypeId ||
														selectBankStatementType.options?.length
															? selectBankStatementType.options
															: [
																	{
																		label:
																			values.input.accountType?.value || '',
																		value: values.input.accountType?.id || 0,
																	},
																]
													}
													onSelect={(value, option) => {
														selectBankStatementType.onSelect!(value, option);
														setFieldValue(
															fieldName(['input', 'accountType']),
															option.data,
														);
													}}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'bankStatementFile'])}
												label="Upload Dokumen"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => {
													const file = value as FileData;
													return {
														fileList: file?.id
															? ([
																	{
																		uid: file.id,
																		name: file.name,
																		thumbUrl: file.url,
																	},
																] as UploadFile[])
															: [],
													};
												}}
												normalize={({ file }: { file: UploadFile }) => {
													return {
														id: file.uid,
														format: file.type,
														name: file.name,
														url: file.thumbUrl,
													} as FileData;
												}}
											>
												<Upload.Dragger
													listType="picture"
													maxCount={1}
													beforeUpload={() => false}
													onRemove={() => {
														form.setFieldValue(
															fieldName(['input', 'bankStatementFile']),
															null,
														);
														return false;
													}}
													onChange={async ({ file }) => {
														const fileid = Date.now().toString();
														const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/bank-statements`;

														try {
															const url = await uploadMutation.mutateAsync({
																file: file as unknown as File,
																fileid,
																filepath,
															});

															form.setFieldValue(
																fieldName(['input', 'bankStatementFile']),
																{
																	format: 'jpeg',
																	id: fileid,
																	name: file.name,
																	path: filepath,
																	url,
																},
															);
														} catch {
															form.setFieldValue(
																fieldName(['input', 'bankStatementFile']),
																null,
															);
														}
													}}
												>
													{uploadMutation.isPending ? (
														<LoadingOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													) : (
														<UploadOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													)}
												</Upload.Dragger>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'bankName'])}
												noStyle
											/>
											<Form.Item
												label="Nama Bank"
												name={fieldName(['input', 'bankId'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Select
													{...selectMasterBank}
													onSelect={(value, option) => {
														selectMasterBank.onSelect!(value, option);
														setFieldValue(
															fieldName(['input', 'bankName']),
															option.data,
														);
													}}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Periode Rekening"
												name={fieldName(['input', 'bankStatementPeriod'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => {
													let dateValue = dayjs();
													if (typeof value === 'string') {
														dateValue = dayjs(value);
													} else {
														dateValue = value;
													}
													return {
														value: dateValue,
													};
												}}
												normalize={(value) => value && dayjs(value)}
											>
												<DatePicker.MonthPicker />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="Account No"
												name={fieldName(['input', 'accountNo'])}
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</FleetModal>
							</Space>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FRMPraSurveyInputBankStatementForm;
